<template>
  <!-- ================================================================
       Dialog thanh toán phiếu thu
       Sử dụng: <invoice-payment-dialog ref="invoicePaymentDialog" @saved="handleSearch" />
       Mở:      this.$refs.invoicePaymentDialog.open(row)
       ================================================================ -->
  <dialog-base
      :visible.sync="visible"
      :title="dialogTitle"
      size="xl"
      :show-confirm="false"
      :show-cancel="false"
      :use-panel="false"
      top="2vh"
  >
    <!-- ─── Panel thông tin học sinh ─────────────────────────── -->
    <div class="ipd-student-card mb-4">
      <div class="ipd-student-profile-section">
        <div class="ipd-student-profile">
          <div class="ipd-student-avatar">
            <i class="el-icon-user" />
          </div>
          <div class="ipd-student-meta">
            <h3 class="ipd-student-name">{{ invoiceData.fullName || 'Đang tải...' }}</h3>
            <div class="ipd-student-badges">
              <span class="ipd-badge ipd-badge--code">Mã HS: {{ invoiceData.studentCode || '—' }}</span>
              <el-tag :type="statusTagType(invoiceData.status)" size="small" effect="dark" class="ipd-status-tag">
                {{ statusLabel(invoiceData.status) }}
              </el-tag>
              <el-tag :type="invoiceData.onlinePaymentStatus ? 'success' : 'info'" size="small" effect="light" class="ipd-status-tag">
                {{ invoiceData.onlinePaymentStatus ? 'Đã thanh toán Online' : 'Chưa thanh toán Online' }}
              </el-tag>
            </div>
          </div>
        </div>
        <div class="ipd-student-info-grid">
          <div class="ipd-info-item">
            <span class="ipd-info-label">Mã phiếu thu</span>
            <span class="ipd-info-value">{{ invoiceData.invoiceCode || '—' }}</span>
          </div>
          <div class="ipd-info-item">
            <span class="ipd-info-label">Kỳ thu / Đợt</span>
            <span class="ipd-info-value">Tháng {{ invoiceData.month }}/{{ invoiceData.year }} &mdash; Đợt {{ invoiceData.turn }}</span>
          </div>
        </div>
      </div>

      <!-- QR Code ở góc phải của header card -->
      <div v-if="urlOnlinePayment" class="ipd-header-qr-section">
        <span class="ipd-header-qr-label">Thanh toán trực tuyến</span>
        <div class="ipd-header-qr-wrapper">
          <el-image
              class="ipd-header-qr-image"
              :src="urlOnlinePayment"
              :preview-src-list="[urlOnlinePayment]"
              fit="contain"
          >
            <div slot="error" class="ipd-qr-image-error">
              <i class="el-icon-picture-outline" />
            </div>
          </el-image>
        </div>
      </div>
    </div>

    <!-- ─── Số tiền tính toán ────────────────────────────────── -->
    <div class="ipd-calc-total">
      <div class="ipd-col-settings">
        <el-popover placement="bottom-start" width="200" trigger="click">
          <div style="display: flex; flex-direction: column; gap: 8px;">
            <el-checkbox v-model="visibleColumns.stt">STT</el-checkbox>
            <el-checkbox v-model="visibleColumns.revenueName">Tên khoản thu</el-checkbox>
            <el-checkbox v-model="visibleColumns.issuingElectronicInvoices">HĐĐT</el-checkbox>
            <el-checkbox v-model="visibleColumns.unit">Đơn vị</el-checkbox>
            <el-checkbox v-model="visibleColumns.quantity">Số lượng</el-checkbox>
            <el-checkbox v-model="visibleColumns.price">Đơn giá</el-checkbox>
            <el-checkbox v-model="visibleColumns.taxPercentage">Thuế</el-checkbox>
            <el-checkbox v-model="visibleColumns.discount">Giảm giá</el-checkbox>
            <el-checkbox v-model="visibleColumns.amount">Thành tiền</el-checkbox>
            <el-checkbox v-model="visibleColumns.description">Ghi chú</el-checkbox>
            <el-checkbox v-model="visibleColumns.calculate">Tính toán</el-checkbox>
          </div>
          <el-button slot="reference" type="primary" plain icon="el-icon-setting" size="small" class="ipd-settings-btn">Thiết lập cột</el-button>
        </el-popover>
      </div>
      <div>
        <span>Số tiền tính toán: </span>
        <span v-if="moneyCheckTotal === 0" class="ipd-calc-value ipd-calc-value--zero">0</span>
        <span v-else class="ipd-calc-value">{{ moneyCheckTotal | formatCurrency }}</span>
      </div>
    </div>

    <!-- ─── Bảng chi tiết khoản thu ──────────────────────────── -->
    <el-table
        ref="detailTable"
        :data="flattenedDetails"
        border
        highlight-current-row
        class="sk-table ipd-detail-table"
        :empty-text="'Không có khoản thu'"
        :max-height="320"
        :cell-style="cellStyleMethod"
        :row-class-name="tableRowClassName"
        row-key="rowKey"
        @selection-change="handleSelectionChange"
    >
      <!-- Cột Selection -->
      <el-table-column
          type="selection"
          width="55"
          align="center"
      />
      <!-- Cột STT -->
      <el-table-column v-if="visibleColumns.stt" label="STT" width="60" align="center">
        <template slot-scope="{ row }">
          <span :class="{ 'ipd-child-index': row._isChild }">{{ row._displayIndex }}</span>
        </template>
      </el-table-column>

      <!-- Cột Tên khoản thu -->
      <el-table-column v-if="visibleColumns.revenueName" label="Tên khoản thu" min-width="200" align="left">
        <template slot-scope="{ row }">
          <div :style="{ paddingLeft: `${(row._depth || 0) * 24}px` }" class="ipd-name-cell">
            <!-- Toggle button for parent with children -->
            <el-button
                v-if="row._hasChildren"
                type="text"
                size="mini"
                class="ipd-toggle-btn"
                @click="toggleExpand(row._parentIdx)"
            >
              <i :class="expandedMap[row._parentIdx] ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" />
            </el-button>
            <span :class="{ 'ipd-parent-name': !row._isChild, 'ipd-child-name': row._isChild }">
              {{ row.revenueName }}
            </span>
          </div>
        </template>
      </el-table-column>

      <!-- Cột HĐĐT -->
      <el-table-column v-if="visibleColumns.issuingElectronicInvoices" label="HĐĐT" width="70" align="center">
        <template slot-scope="{ row }">
          <el-checkbox v-model="row.issuingElectronicInvoices" :disabled="row._isChild" />
        </template>
      </el-table-column>

      <!-- Cột Đơn vị -->
      <el-table-column v-if="visibleColumns.unit" label="Đơn vị" prop="unit" width="90" align="center">
        <template slot-scope="{ row }">
          <span>{{ row.unit || '—' }}</span>
        </template>
      </el-table-column>

      <!-- Cột Số lượng -->
      <el-table-column v-if="visibleColumns.quantity" label="Số lượng" width="90" align="center">
        <template slot-scope="{ row }">
          <span>{{ formatQuantity(row.quantity) }}</span>
        </template>
      </el-table-column>

      <!-- Cột Đơn giá -->
      <el-table-column v-if="visibleColumns.price" label="Đơn giá" width="140" align="right">
        <template slot-scope="{ row }">
          <span>{{ row.price | formatCurrency }}</span>
        </template>
      </el-table-column>

      <!-- Cột Thuế -->
      <el-table-column v-if="visibleColumns.taxPercentage" label="Thuế" min-width="120" align="center">
        <template slot-scope="{ row }">
          <span>{{ formatTax(row.taxPercentage) }}</span>
        </template>
      </el-table-column>

      <!-- Cột Giảm giá -->
      <el-table-column v-if="visibleColumns.discount" label="Giảm giá" width="130" align="right">
        <template slot-scope="{ row }">
          <span v-if="row.discount" :class="row.discountType === 'PERCENT' ? '' : ''">
            {{ row.discountNumber | formatCurrency }}
            <span v-if="row.discountType === 'PERCENT'" class="ipd-text-muted"> (%)</span>
          </span>
          <span v-else class="ipd-text-muted">—</span>
        </template>
      </el-table-column>

      <!-- Cột Thành tiền -->
      <el-table-column v-if="visibleColumns.amount" label="Thành tiền" width="140" align="right">
        <template slot-scope="{ row }">
          <span class="ipd-money--bold">{{ row.amount | formatCurrency }}</span>
        </template>
      </el-table-column>

      <!-- Cột Ghi chú -->
      <el-table-column v-if="visibleColumns.description" label="Ghi chú" min-width="180" align="left" class-name="ipd-text-left">
        <template slot-scope="{ row }">
          <div v-if="row.description">
            <div
                :class="{'ipd-desc-clamped': !expandedDescMap[row.rowKey], 'ipd-desc-expanded': expandedDescMap[row.rowKey]}"
                class="ipd-desc-text"
                :title="!expandedDescMap[row.rowKey] ? row.description : ''"
            >
              {{ row.description.trim() }}
            </div>
            <el-button
                v-if="row.description.length > 60 && !expandedDescMap[row.rowKey]"
                type="text"
                size="mini"
                class="ipd-btn-more"
                @click="$set(expandedDescMap, row.rowKey, true)"
            >
              Xem thêm
            </el-button>
            <el-button
                v-if="row.description.length > 60 && expandedDescMap[row.rowKey]"
                type="text"
                size="mini"
                class="ipd-btn-more"
                @click="$set(expandedDescMap, row.rowKey, false)"
            >
              Thu gọn
            </el-button>
          </div>
          <span v-else class="ipd-text-muted">—</span>
        </template>
      </el-table-column>

      <!-- Cột Tính toán (checkbox) -->
      <el-table-column v-if="visibleColumns.calculate" fixed="right" align="center" width="100">
        <template slot="header">
          <div>Tính toán</div>
          <el-checkbox v-model="checkAll" @change="changeAllCheckbox" />
        </template>
        <template slot-scope="{ row }">
          <el-checkbox
              v-if="!row._isChild"
              v-model="row._checked"
              @change="changeCheckbox(row)"
          />
        </template>
      </el-table-column>
    </el-table>

    <!-- Hướng dẫn Hóa đơn điện tử -->
    <div
        v-if="checkPermission(['electronic-invoice-create-issued', 'electronic-invoice-create-draft'])"
        class="ipd-invoice-banner"
    >
      <div class="ipd-invoice-banner-icon">
        <i class="el-icon-warning-outline" />
      </div>
      <div class="ipd-invoice-banner-body">
        <h4 class="ipd-invoice-banner-title">Lưu ý về Hóa đơn điện tử (HĐĐT):</h4>
        <div class="ipd-invoice-banner-text">
          <p>
            1. Chức năng <strong>Tự động lập hóa đơn nháp trên Onekids</strong> chỉ được khởi tạo cho những <strong>khoản chính</strong> đã được đăng ký khai báo hóa đơn điện tử.
          </p>
          <p style="margin-top: 4px;">
            2. Nếu chưa đăng ký tự động, bạn có thể lập thủ công bằng cách sử dụng nút <strong>Lập HĐ điện tử</strong> phía dưới. Những khoản được tích chọn sẽ được chuyển sang hóa đơn điện tử (lưu ý thực hiện <strong>khai báo thuế</strong> đầy đủ cho các khoản đó).
          </p>
        </div>
      </div>
    </div>

    <!-- ─── Khu vực tổng kết + Form thanh toán ─────────────── -->
    <div class="ipd-payment-area">
      <el-row :gutter="20">
        <!-- Cột trái: Tổng kết tài chính -->
        <el-col :span="7">
          <div class="ipd-summary-cards">
            <!-- Thừa/Thiếu phiếu thu trước -->
            <div class="ipd-summary-card" :class="invoiceData.beforeMoney < 0 ? 'ipd-card--danger' : invoiceData.beforeMoney > 0 ? 'ipd-card--success' : 'ipd-card--neutral'">
              <div class="ipd-card-left">
                <div class="ipd-card-icon">
                  <i :class="invoiceData.beforeMoney < 0 ? 'el-icon-info' : 'el-icon-circle-check'" />
                </div>
                <span class="ipd-card-label">{{ invoiceData.beforeMoney >= 0 ? 'Thừa phiếu thu trước' : 'Thiếu phiếu thu trước' }}</span>
              </div>
              <span class="ipd-card-value">
                <template v-if="invoiceData.beforeMoney === 0">0 đ</template>
                <template v-else>{{ Math.abs(invoiceData.beforeMoney) | formatCurrency }}</template>
              </span>
            </div>

            <!-- Tổng phiếu thu -->
            <div class="ipd-summary-card ipd-card--primary">
              <div class="ipd-card-left">
                <div class="ipd-card-icon">
                  <i class="el-icon-document" />
                </div>
                <span class="ipd-card-label">Tổng tiền phiếu thu</span>
              </div>
              <span class="ipd-card-value">
                <template v-if="invoiceData.sumMoney === 0">0 đ</template>
                <template v-else>{{ invoiceData.sumMoney | formatCurrency }}</template>
              </span>
            </div>

            <!-- Đã trả -->
            <div class="ipd-summary-card ipd-card--success-light">
              <div class="ipd-card-left">
                <div class="ipd-card-icon">
                  <i class="el-icon-circle-check" />
                </div>
                <span class="ipd-card-label">Tổng tiền đã đóng</span>
              </div>
              <span class="ipd-card-value">
                <template v-if="invoiceData.collectedMoney === 0">0 đ</template>
                <template v-else>{{ invoiceData.collectedMoney | formatCurrency }}</template>
              </span>
            </div>

            <!-- Tiền còn lại phải trả -->
            <div class="ipd-summary-card" :class="afterMoney < 0 ? 'ipd-card--warning-dark' : 'ipd-card--success-dark'">
              <div class="ipd-card-left">
                <div class="ipd-card-icon">
                  <i class="el-icon-wallet" />
                </div>
                <span class="ipd-card-label">{{ afterMoney >= 0 ? 'Còn dư được nhận' : 'Còn lại phải đóng' }}</span>
              </div>
              <span class="ipd-card-value">
                <template v-if="afterMoney === 0">0 đ</template>
                <template v-else>{{ Math.abs(afterMoney) | formatCurrency }}</template>
              </span>
            </div>

            <!-- Tổng tiền nhập -->
            <div class="ipd-summary-card ipd-card--info">
              <div class="ipd-card-left">
                <div class="ipd-card-icon">
                  <i class="el-icon-edit" />
                </div>
                <span class="ipd-card-label">Tổng tiền đang nhập</span>
              </div>
              <span class="ipd-card-value">
                <template v-if="totalMoneyInput === 0">0 đ</template>
                <template v-else>{{ totalMoneyInput | formatCurrency }}</template>
              </span>
            </div>

            <!-- Tiền thừa thiếu sau khi nhập -->
            <div class="ipd-summary-card" :class="afterMoneyThisMonth < 0 ? 'ipd-card--danger' : afterMoneyThisMonth > 0 ? 'ipd-card--highlight' : 'ipd-card--neutral'">
              <div class="ipd-card-left">
                <div class="ipd-card-icon">
                  <i class="el-icon-money" />
                </div>
                <span class="ipd-card-label">{{ afterMoneyThisMonth >= 0 ? 'Số tiền thừa' : 'Số tiền còn thiếu' }}</span>
              </div>
              <span class="ipd-card-value">
                <template v-if="afterMoneyThisMonth === 0">0 đ</template>
                <template v-else>{{ Math.abs(afterMoneyThisMonth) | formatCurrency }}</template>
              </span>
            </div>
          </div>
        </el-col>

        <!-- Cột phải: Form nhập liệu -->
        <el-col :span="17">
          <el-form
              ref="paymentForm"
              :model="paymentData"
              :rules="paymentRules"
              label-width="155px"
              label-position="left"
          >
            <div class="ipd-form-row">
              <el-form-item label="Tiền mặt" prop="cashMoney" class="ipd-form-item-half">
                <el-currency-input
                    :options="currencyOption"
                    v-model="cashMoney"
                    placeholder="Nhập tiền mặt"
                />
              </el-form-item>
              <el-form-item label="Chuyển khoản" prop="transferMoney" class="ipd-form-item-half ipd-form-item-right">
                <el-currency-input
                    :options="currencyOption"
                    v-model="transferMoney"
                    placeholder="Nhập tiền chuyển khoản"
                />
              </el-form-item>
            </div>

            <div class="ipd-form-row">
              <el-form-item label="Người thanh toán" prop="parentPay" class="ipd-form-item-half">
                <el-input v-model="paymentData.parentPay" placeholder="Phụ huynh học sinh" />
              </el-form-item>
              <el-form-item label="Ngày thanh toán" prop="datePayment" class="ipd-form-item-half ipd-form-item-right">
                <el-date-picker
                    v-model="paymentData.datePayment"
                    type="date"
                    :clearable="false"
                    style="width: 100%"
                    value-format="yyyy-MM-dd"
                    format="dd-MM-yyyy"
                    placeholder="Chọn ngày"
                    :picker-options="pickerOptions"
                />
              </el-form-item>
            </div>

            <el-form-item label="Tiền thừa thiếu" prop="transferMoneyType" class="ipd-form-item-radio-button">
              <el-radio-group v-model="paymentData.transferMoneyType" size="medium" class="ipd-radio-buttons">
                <el-radio-button label="MONEY_WALLET">Không chuyển</el-radio-button>
                <el-radio-button label="MONEY_MONTH">Sang phiếu thu sau</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <el-form-item label="Mô tả" prop="noteHistory">
              <el-input v-model="paymentData.noteHistory" placeholder="Nhập mô tả" />
            </el-form-item>

            <el-form-item>
              <el-checkbox v-if="isActivatedAccount" v-model="paymentData.statusSendNotify">
                Gửi thông báo thanh toán trên App
              </el-checkbox>
              <span v-else style="color: #d13b93">
                Tài khoản chưa được kích hoạt, không thể gửi thông báo thanh toán App
              </span>
            </el-form-item>

            <el-form-item  v-if="checkPermission(['electronic-invoice-create-issued', 'electronic-invoice-create-draft'])">
              <el-checkbox v-model="paymentData.draftInvoice">
                Lập hoá đơn nháp trên Onekids
              </el-checkbox>
            </el-form-item>
          </el-form>
        </el-col>
      </el-row>
    </div>

    <template #footer>
      <div class="sk-dialog-footer">
        <el-button
            type="primary"
            size="medium"
            class="ipd-btn-primary"
            :loading="loadingPayment"
            @click="handleSubmitPayment"
        >
          <i class="el-icon-circle-check" />
          <span>Thanh toán</span>
        </el-button>

        <el-button
            v-if="checkPermission(['electronic-invoice-create-draft'])"
            type="warning"
            plain
            size="medium"
            class="ipd-btn-warning-plain"
            @click="handleCreateElectronicInvoice"
        >
          <i class="el-icon-document" />
          <span>Lập HĐ điện tử</span>
        </el-button>

        <el-button
            type="success"
            plain
            size="medium"
            class="ipd-btn-success-plain"
            :loading="loadingPrint"
            @click="handlePrintReceipt"
        >
          <i class="el-icon-printer" />
          <span>In phiếu thu</span>
        </el-button>

        <el-button type="info" plain size="medium" class="ipd-btn-close" @click="handleClose">
          <i class="el-icon-circle-close" />
          <span>Đóng</span>
        </el-button>
      </div>
    </template>

    <!-- Dialog tạo HĐĐT -->
    <InvoiceCreateDialog
        :isOpenInvoiceCreateDialog.sync="invoiceCreateDialogVisible"
        :initialItems="invoiceInitialItems"
        :kidCode="invoiceData.studentCode"
        :orderKidCode="invoiceData.invoiceCode"
        @dialog-close="handleCloseInvoiceCreateDialog"
        @back="handleCloseInvoiceCreateDialog"
    />
  </dialog-base>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import InvoiceCreateDialog from "@/components/sinvoice_viettel/dialog/InvoiceCreateDialog.vue";
import ManagingStudentInvoiceService from "@/services/tutition_v3/ManagingStudentInvoiceService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import checkPermission from "@/utils/permission";
import moment from "moment";
import { formatQuantity as formatQuantityValue } from "@/components/tutition_v3/utils/quantity";

const STATUS_OPTIONS = [
  { value: "UNBILLED", label: "Chưa có phiếu thu" },
  { value: "UNPAID", label: "Chưa đóng 100%" },
  { value: "PARTIALLY_PAID", label: "Chưa đóng đủ" },
  { value: "FULLY_PAID", label: "Đóng đủ 100%" },
  { value: "OVERPAID", label: "Đóng thừa" },
  { value: "FULLY_PAID_WITH_SURPLUS", label: "Đóng đủ thừa" },
  { value: "PAID_WITH_DEFICIT", label: "Đóng đủ thiếu" },
];

/**
 * Mock data giả lập chi tiết phiếu thu.
 * Khi BE hoàn thiện API, xóa hàm này và dùng response thật.
 */


export default {
  name: "InvoicePaymentDialog",
  components: { DialogBase, InvoiceCreateDialog },

  data() {
    return {
      visible: false,
      isLoading: false,
      loadingPayment: false,
      loadingPrint: false,
      multipleSelection: [],
      urlOnlinePayment: null,
      isActivatedAccount: false,

      // Invoice Create Dialog State
      invoiceCreateDialogVisible: false,
      invoiceInitialItems: [],

      // Invoice data from API
      invoiceData: {
        idx: "",
        fullName: "",
        nickName: "",
        studentCode: "",
        invoiceCode: "",
        turn: null,
        month: null,
        year: null,
        status: "",
        startValidityPeriod: "",
        endValidityPeriod: "",
        sumMoney: 0,
        beforeMoney: 0,
        collectedMoney: 0,
        cashMoney: 0,
        transferMoney: 0,
        afterMoney: 0,
        onlinePaymentStatus: false,
        invoiceDetails: [],
        urlOnlinePayment: "",
        bankAccount: "",
        bankOwner: "",
        bankName: "",
      },

      // Expand/collapse map: parentIdx -> boolean
      expandedMap: {},

      // Checkbox state per parent index
      checkAll: true,
      expandedDescMap: {},

      visibleColumns: {
        stt: true,
        revenueName: true,
        issuingElectronicInvoices: true,
        unit: true,
        quantity: true,
        price: true,
        taxPercentage: true,
        discount: true,
        description: true,
        amount: true,
        calculate: true,
      },

      // Payment form
      cashMoney: 0,
      transferMoney: 0,
      paymentData: {
        parentPay: "PHHS",
        datePayment: moment(new Date()).format("YYYY-MM-DD"),
        transferMoneyType: "MONEY_WALLET",
        noteHistory: "",
        statusSendNotify: false,
        draftInvoice: false,
      },

      currencyOption: {
        currency: "VND",
        locale: "de",
        allowNegative: false,
      },

      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now();
        },
      },

      paymentRules: {
        parentPay: [
          { required: true, message: "Người thanh toán không được để trống!", trigger: "blur" },
        ],
      },
    };
  },

  computed: {
    dialogTitle() {
      if (!this.invoiceData.fullName) return "Thanh toán phiếu thu";
      return `Thanh toán phiếu thu: ${this.invoiceData.fullName} - ${this.invoiceData.invoiceCode} - Đợt ${this.invoiceData.turn}`;
    },

    /**
     * afterMoney = beforeMoney + sumMoney - collectedMoney (phải trả còn lại)
     * Dùng giá trị từ API
     */
    afterMoney() {
      return Number(this.invoiceData.afterMoney || 0);
    },

    totalMoneyInput() {
      return (this.cashMoney || 0) + (this.transferMoney || 0);
    },

    afterMoneyThisMonth() {
      return this.afterMoney + this.totalMoneyInput;
    },

    moneyCheckTotal() {
      return this.flattenedDetails
          .filter((row) => !row._isChild && row._checked)
          .reduce((sum, row) => sum + Number(row.amount || 0), 0);
    },

    /**
     * Flatten invoiceDetails thành mảng phẳng, bao gồm cả hàng cha và hàng con.
     * Hàng con chỉ hiển thị khi cha đang expand.
     */
    flattenedDetails() {
      const result = [];
      const details = this.invoiceData.invoiceDetails || [];
      const alphabet = "abcdefghijklmnopqrstuvwxyz";

      const appendChildren = (children, parentIndex, path = [], depth = 1, displayPrefix = "") => {
        children.forEach((child, childIndex) => {
          const childPath = [...path, childIndex];
          const displayIndex = depth === 1
            ? (alphabet[childIndex] || `${childIndex + 1}`)
            : `${displayPrefix}.${childIndex + 1}`;

          result.push({
            ...child,
            rowKey: `child-${parentIndex}-${childPath.join("-")}`,
            _displayIndex: displayIndex,
            _isChild: true,
            _depth: depth,
            _hasChildren: false,
            _parentIdx: parentIndex,
            _checked: false,
          });

          if (Array.isArray(child.invoiceDetailItems) && child.invoiceDetailItems.length > 0) {
            appendChildren(child.invoiceDetailItems, parentIndex, childPath, depth + 1, displayIndex);
          }
        });
      };

      details.forEach((parent, parentIndex) => {
        const hasChildren =
            Array.isArray(parent.invoiceDetailItems) &&
            parent.invoiceDetailItems.length > 0;

        result.push({
          ...parent,
          rowKey: `parent-${parentIndex}`,
          _displayIndex: `${parentIndex + 1}`,
          _isChild: false,
          _depth: 0,
          _hasChildren: hasChildren,
          _parentIdx: parentIndex,
          _checked: parent._checked !== undefined ? parent._checked : true,
        });

        // Add children if parent is expanded
        if (hasChildren && this.expandedMap[parentIndex]) {
          appendChildren(parent.invoiceDetailItems, parentIndex);
        }
      });

      return result;
    },
  },

  watch: {
    cashMoney(val) {
      if (val < 0) {
        this.$nextTick(() => {
          this.cashMoney = 0;
        });
      }
    },
    transferMoney(val) {
      if (val < 0) {
        this.$nextTick(() => {
          this.transferMoney = 0;
        });
      }
    },
  },

  methods: {
    formatQuantity(value) {
      return formatQuantityValue(value);
    },

    formatTax(val) {
      if (val === null || val === undefined || val === '') return '—';
      const tax = Number(val);
      if (tax === -2) return 'Không chịu thuế';
      if (tax === -1) return 'Không kê khai tính/nộp thuế';
      return `${tax}%`;
    },

    // ─── Public API ──────────────────────────────────────────────
    /**
     * Mở dialog thanh toán phiếu thu
     * @param {Object} row - Dữ liệu dòng phiếu thu từ bảng chính
     */
    open(row) {
      this.resetForm();
      this.visible = true;
      this.fetchInvoiceDetail(row);
    },

    // ─── API calls ───────────────────────────────────────────────
    async fetchInvoiceDetail(row) {
      this.isLoading = true;
      try {
        // TODO: Khi BE hoàn thiện, dùng API thật thay vì mock data
        const res = await ManagingStudentInvoiceService.getInvoiceDetail(row.idx);
        const data = res?.data?.data;

        // // Giả lập dữ liệu
        // const data = buildMockInvoiceDetail(row);

        this.invoiceData = {
          ...data,
          sumMoney: Number(data.sumMoney || 0),
          beforeMoney: Number(data.beforeMoney || 0),
          collectedMoney: Number(data.collectedMoney || 0),
          cashMoney: Number(data.cashMoney || 0),
          transferMoney: Number(data.transferMoney || 0),
          afterMoney: Number(data.afterMoney || 0),
          onlinePaymentStatus: !!data.onlinePaymentStatus,
          invoiceDetails: (data.invoiceDetails || []).map((d) => ({
            ...d,
            _checked: true,
          })),
        };

        // Initialize expandedMap: all parents with children are expanded by default
        const expanded = {};
        this.invoiceData.invoiceDetails.forEach((detail, index) => {
          if (
              Array.isArray(detail.invoiceDetailItems) &&
              detail.invoiceDetailItems.length > 0
          ) {
            expanded[index] = true;
          }
        });
        this.expandedMap = expanded;
        this.checkAll = true;
        this.urlOnlinePayment = data.urlOnlinePayment || null;
        this.isActivatedAccount = data.isActivatedAccount !== undefined ? data.isActivatedAccount : false;

        this.$nextTick(() => {
          if (this.$refs.detailTable) {
            this.flattenedDetails.forEach((row) => {
              if (!row._isChild) {
                this.$refs.detailTable.toggleRowSelection(row, true);
              }
            });
          }
        });

        if (data.parentPay) {
          this.paymentData.parentPay = data.parentPay;
        }
        if (data.datePayment) {
          this.paymentData.datePayment = data.datePayment;
        }
        if (data.transferMoneyType) {
          this.paymentData.transferMoneyType = data.transferMoneyType;
        }
        if (data.statusSendNotify !== undefined) {
          this.paymentData.statusSendNotify = !!data.statusSendNotify;
        }
        if (data.draftInvoice !== undefined) {
          this.paymentData.draftInvoice = !!data.draftInvoice;
        }
      } catch (err) {
        console.error(err);
        NotifyMessage.leftCornerNotificationError(
            (err?.response?.data?.message) || "Không thể tải chi tiết phiếu thu!"
        );
      } finally {
        this.isLoading = false;
      }
    },

    // ─── Expand/Collapse ─────────────────────────────────────────
    toggleExpand(parentIdx) {
      this.$set(this.expandedMap, parentIdx, !this.expandedMap[parentIdx]);
    },

    // ─── Checkbox ────────────────────────────────────────────────
    changeAllCheckbox() {
      const details = this.invoiceData.invoiceDetails || [];
      details.forEach((d) => {
        this.$set(d, "_checked", this.checkAll);
      });
    },

    changeCheckbox(row) {
      const parent = this.invoiceData.invoiceDetails[row._parentIdx];
      if (parent) {
        this.$set(parent, "_checked", row._checked);
      }
      const details = this.invoiceData.invoiceDetails || [];
      this.checkAll = details.every((d) => d._checked);
    },

    // ─── Selection ───────────────────────────────────────────────────
    handleSelectionChange(val) {
      this.multipleSelection = val;
    },

    // ─── Status helpers ──────────────────────────────────────────
    statusLabel(status) {
      const item = STATUS_OPTIONS.find((o) => o.value === status);
      return item ? item.label : status || "—";
    },

    statusTagType(status) {
      const map = {
        UNBILLED: "info",
        UNPAID: "danger",
        PARTIALLY_PAID: "warning",
        FULLY_PAID: "success",
        OVERPAID: "success",
        FULLY_PAID_WITH_SURPLUS: "success",
        PAID_WITH_DEFICIT: "warning",
      };
      return map[status] || "info";
    },

    moneyColorClass(value) {
      if (value === 0) return "ipd-summary-value--zero";
      if (value < 0) return "ipd-summary-value--negative"; // Thiếu/Nợ (Đỏ)
      return "ipd-summary-value--positive"; // Thừa/Được nhận (Xanh)
    },

    cellStyleMethod() {
      return {};
    },

    tableRowClassName({ row }) {
      const classes = [];
      if (row._isChild) {
        classes.push('ipd-row-child');
      } else {
        classes.push('ipd-row-parent');
        if (row._checked) {
          classes.push('ipd-row-checked');
        }
      }
      return classes.join(' ');
    },

    // ─── Payment actions ─────────────────────────────────────────
    async handleSubmitPayment() {
      if (this.invoiceData.onlinePaymentStatus) {
        NotifyMessage.leftCornerNotificationWarning("Phiếu thu này đã được thanh toán trực tuyến.");
        return;
      }

      const valid = await this.$refs.paymentForm.validate().catch(() => false);
      if (!valid) return;

      if ((this.cashMoney || 0) < 0 || (this.transferMoney || 0) < 0) {
        NotifyMessage.leftCornerNotificationWarning("Số tiền thanh toán không được là số âm!");
        return;
      }

      // if (this.totalMoneyInput === 0) {
      //   NotifyMessage.leftCornerNotificationWarning("Vui lòng nhập số tiền thanh toán!");
      //   return;
      // }

      const moneyFormatted = this.$funcCommon
          ? this.$funcCommon.formatMoney(this.totalMoneyInput)
          : this.totalMoneyInput.toLocaleString("vi-VN");

      this.$confirm(
          `Bạn có chắc chắn muốn thanh toán với tổng tiền là ${moneyFormatted} VNĐ`,
          "Xác nhận thanh toán",
          {
            distinguishCancelAndClose: true,
            confirmButtonText: "Có",
            closeOnClickModal: false,
            cancelButtonText: "Không",
          }
      )
          .then(async () => {
            this.loadingPayment = true;
            try {
              const payload = {
                idx: this.invoiceData.idx,
                idStudent: this.invoiceData.idStudent,
                cashMoney: this.cashMoney || 0,
                transferMoney: this.transferMoney || 0,
                collectedMoney: this.totalMoneyInput,
                afterMoney: this.afterMoneyThisMonth,
                parentPay: this.paymentData.parentPay,
                datePayment: this.paymentData.datePayment,
                transferMoneyType: this.paymentData.transferMoneyType,
                noteHistory: this.paymentData.noteHistory,
                statusSendNotify: this.paymentData.statusSendNotify,
                draftInvoice: this.paymentData.draftInvoice,
                turn: this.invoiceData.turn,
              };

              // TODO: Khi BE hoàn thiện, bật API thật
              await ManagingStudentInvoiceService.payInvoice(payload);

              NotifyMessage.leftCornerNotificationSuccess("Thanh toán thành công!");
            } catch (err) {
              console.error(err);
              NotifyMessage.leftCornerNotificationError(
                  err?.response?.data?.message || "Thanh toán thất bại!"
              );
            } finally {
              this.loadingPayment = false;
              this.handleClose()
            }
          })
          .catch(() => {
            // User cancelled
          });
    },

    handlePrintReceipt() {
      if (this.multipleSelection.length === 0) {
        NotifyMessage.leftCornerNotificationWarning("Vui lòng chọn ít nhất một khoản thu để in.");
        return;
      }
      this.loadingPrint = true;
      try {
        // Sắp xếp lại selectedItems theo thứ tự của flattenedDetails
        const selectedIds = this.multipleSelection.map(item => item.rowKey);
        const orderedSelectedItems = this.flattenedDetails.filter(item => selectedIds.includes(item.rowKey));

        const payload = {
          invoiceInfo: this.invoiceData,
          selectedItems: orderedSelectedItems
        };
        localStorage.setItem("tuition_invoice_print_data", JSON.stringify(payload));
        window.open("/tuition-invoice-print", "_blank");
      } catch (e) {
        NotifyMessage.leftCornerNotificationError("Có lỗi xảy ra khi chuẩn bị dữ liệu in.");
      } finally {
        setTimeout(() => {
          this.loadingPrint = false;
        }, 500);
      }
    },

    // ─── Close & Reset ───────────────────────────────────────────
    handleClose() {
      this.visible = false;
      this.$emit("saved");
    },

    resetForm() {
      this.cashMoney = 0;
      this.transferMoney = 0;
      this.loadingPayment = false;
      this.loadingPrint = false;
      this.checkAll = true;
      this.expandedMap = {};
      this.expandedDescMap = {};
      this.visibleColumns = {
        stt: true,
        revenueName: true,
        issuingElectronicInvoices: true,
        unit: true,
        quantity: true,
        price: true,
        taxPercentage: true,
        discount: true,
        description: true,
        amount: true,
        calculate: true,
      };
      this.multipleSelection = [];
      this.urlOnlinePayment = null;
      this.isActivatedAccount = false;
      this.invoiceData = {
        idx: "",
        fullName: "",
        nickName: "",
        studentCode: "",
        invoiceCode: "",
        turn: null,
        month: null,
        year: null,
        status: "",
        startValidityPeriod: "",
        endValidityPeriod: "",
        sumMoney: 0,
        beforeMoney: 0,
        collectedMoney: 0,
        cashMoney: 0,
        transferMoney: 0,
        afterMoney: 0,
        onlinePaymentStatus: false,
        invoiceDetails: [],
      };
      this.paymentData = {
        parentPay: "PHHS",
        datePayment: moment(new Date()).format("YYYY-MM-DD"),
        transferMoneyType: "MONEY_WALLET",
        noteHistory: "",
        statusSendNotify: false,
        draftInvoice: false,
      };
      this.$nextTick(() => {
        this.$refs.paymentForm && this.$refs.paymentForm.resetFields();
      });
    },

    // ─── Electronic Invoice ──────────────────────────────────────
    checkPermission,

    handleCreateElectronicInvoice() {
      // Sử dụng multipleSelection (checkbox selection trên bảng) để lấy các khoản đã chọn
      // và lọc thêm theo cột HĐĐT (issuingElectronicInvoices)
      const selectedItems = this.multipleSelection.filter(
          item => !item._isChild && item.issuingElectronicInvoices
      );

      if (selectedItems.length === 0) {
        NotifyMessage.leftCornerNotificationWarning("Vui lòng chọn ít nhất một khoản thu có tích HĐĐT!");
        return;
      }

      const mappedItems = this.mapSelectionToInvoiceItems(selectedItems);
      if (mappedItems.length === 0) {
        NotifyMessage.leftCornerNotificationWarning("Không có khoản thu hợp lệ để lập hóa đơn điện tử!");
        return;
      }

      this.invoiceInitialItems = mappedItems;
      this.invoiceCreateDialogVisible = true;
    },

    /**
     * Map các khoản thu đã chọn sang format mà InvoiceCreateDialog cần.
     * Tham khảo từ mapOrderKidsSelectionToInvoiceItems trong OrderKidsCasher.vue
     */
    mapSelectionToInvoiceItems(selection = []) {
      if (!Array.isArray(selection)) return [];

      const mappedItems = [];
      selection.forEach((row) => {
        if (!row) return;

        const quantity = Number(row.quantity) > 0 ? Number(row.quantity) : 1;
        // Sử dụng đơn giá thực tế (row.price) thay vì chia từ thành tiền (row.amount), tránh tính trùng thuế
        const unitPrice = row.price !== undefined && row.price !== null ? Number(row.price) : (quantity > 0 ? Number(row.amount || 0) / quantity : Number(row.amount || 0));
        // Thành tiền chưa thuế tương ứng
        const totalAmountWithoutTax = unitPrice * quantity;

        // Map thông tin chiết khấu/giảm giá nếu có
        let discount = 0;
        let itemDiscount = 0;
        if (row.discount && row.discountNumber) {
          const discountVal = Number(row.discountNumber) || 0;
          if (String(row.discountType).toUpperCase() === 'PERCENT') {
            discount = discountVal;
          } else {
            itemDiscount = discountVal;
          }
        }

        mappedItems.push({
          id: row.idx || row.id || '',
          itemCode: row.idx || row.id ? String(row.idx || row.id) : null,
          itemName: row.revenueName || '',
          nameKt: row.revenueName || '',
          unitName: row.unit || 'Lần',
          quantity,
          quantityKt: quantity,
          unitPrice,
          priceKt: unitPrice,
          itemTotalAmountWithoutTax: totalAmountWithoutTax,
          moneyKt: totalAmountWithoutTax,
          discount,
          itemDiscount,
          taxPercentage: row.taxPercentage !== undefined && row.taxPercentage !== null ? Number(row.taxPercentage) : 0,
          itemNote: row.description || '',
          raw: row,
        });
      });

      return mappedItems;
    },

    handleCloseInvoiceCreateDialog() {
      this.invoiceCreateDialogVisible = false;
    },

    handleInvoiceSaved() {
      this.invoiceCreateDialogVisible = false;
      this.fetchInvoiceDetail({ idx: this.invoiceData.idx }); // reload if possible
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";
@import "@/assets/style/sk-table.scss";

/* =====================================================
   InvoicePaymentDialog – Prefix: ipd-
   ===================================================== */

/* ─── Tổng tiền tính toán ─────────────────────────────── */
.ipd-calc-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
  color: #475569;
}
.ipd-calc-value {
  font-weight: 700;
  color: #1e293b;
  font-size: 15px;
}
.ipd-calc-value--zero {
  color: #94a3b8;
}

/* ─── Detail table ────────────────────────────────────── */
.ipd-detail-table {
  margin-bottom: 8px;
}

.ipd-name-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ipd-toggle-btn {
  padding: 2px 4px !important;
  font-size: 14px !important;
  color: #2563eb !important;
  min-width: auto !important;
}
.ipd-toggle-btn:hover {
  color: #1d4ed8 !important;
}

.ipd-parent-name {
  font-weight: 600;
  color: #1e293b;
}
.ipd-child-name {
  color: #64748b;
  font-size: 12.5px;
}
.ipd-child-index {
  color: #64748b;
  font-style: italic;
}

.ipd-hddt-check {
  color: #059669;
  font-size: 16px;
  font-weight: 700;
}

.ipd-text-muted {
  color: #94a3b8;
}

.ipd-money--bold {
  font-weight: 700;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
}

/* ─── Payment area ────────────────────────────────────── */
.ipd-payment-area {
  margin-top: 16px;
  padding-top: 16px;
}

/deep/ .ipd-payment-area .el-row {
  background: #e8f0fe;
  border-radius: 10px;
  padding: 16px 12px 8px;
  margin-top: 0;
}

/* ─── Summary box (cột trái) ──────────────────────────── */
.ipd-summary-box {
  padding: 4px 8px;
}

.ipd-summary-row {
  margin-bottom: 12px;
  font-size: 13px;
  color: #1e293b;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}

.ipd-summary-value {
  font-weight: 700;
  white-space: nowrap;
}
.ipd-summary-value--zero {
  color: #94a3b8;
}
.ipd-summary-value--positive {
  color: #2563eb;
}
.ipd-summary-value--negative {
  color: #dc2626;
}
.ipd-summary-value--highlight {
  color: #d97706;
}

.ipd-summary-divider {
  border: none;
  border-bottom: 1px solid #94a3b8;
  margin: 8px 0 12px;
}

/* ─── QR section & Card ───────────────────────────────── */
.ipd-qr-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed #cbd5e1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.ipd-qr-label {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
  text-align: left;
}
.ipd-qr-card {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #dbeafe;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(37, 99, 235, 0.12);
    border-color: #bfdbfe;
  }
}
.ipd-qr-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  color: #1e3a8a;
}
.ipd-qr-icon {
  font-size: 16px;
  color: #2563eb;
}
.ipd-qr-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.ipd-qr-desc {
  font-size: 11px;
  color: #64748b;
  margin: 0 0 12px 0;
  line-height: 1.4;
  text-align: center;
  max-width: 180px;
}
.ipd-qr-image-wrapper {
  position: relative;
  width: 150px;
  height: 150px;
  padding: 6px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;

    .ipd-qr-hint {
      opacity: 1;
      transform: translateY(0);
    }
    .ipd-qr-image {
      filter: brightness(0.9);
    }
  }
}
.ipd-qr-image {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  transition: all 0.3s ease;

  /deep/ img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}
.ipd-qr-image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: #94a3b8;
  font-size: 11px;

  i {
    font-size: 20px;
  }
}
.ipd-qr-hint {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(37, 99, 235, 0.9);
  color: #ffffff;
  font-size: 10.5px;
  font-weight: 500;
  padding: 4px 0;
  text-align: center;
  opacity: 0;
  transform: translateY(100%);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  pointer-events: none;
}

/* ─── Form (cột phải) ─────────────────────────────────── */
.ipd-form-row {
  display: flex;
  gap: 16px;
}
.ipd-form-item-half {
  flex: 1;
}
.ipd-form-item-right {
  // float alignment
}

/deep/ .ipd-payment-area .el-form-item__label {
  color: #1e293b;
  font-weight: 600;
}

/deep/ .ipd-payment-area .el-input__inner {
  border-radius: 8px;
}

/* ─── Table header: xanh primary ──────────────────────── */
/deep/ .ipd-detail-table.sk-table thead th,
/deep/ .ipd-detail-table.sk-table thead td {
  background: #2563eb !important;
  color: #ffffff !important;
  font-weight: 700;
}
/deep/ .ipd-detail-table.sk-table thead th > .cell,
/deep/ .ipd-detail-table.sk-table thead td > .cell {
  color: #ffffff !important;
  font-weight: 700;
}

/* ─── Footer buttons ──────────────────────────────────── */
.sk-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* ─── Column Settings Button ────────────────────────────── */
.ipd-settings-btn {
  font-weight: 600;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.15);
  transition: all 0.2s;
}
.ipd-settings-btn:hover {
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
  transform: translateY(-1px);
}

/* ─── Ghi chú Expand/Collapse ───────────────────────────── */
/deep/ .ipd-text-left .cell {
  text-align: left !important;
}
.ipd-desc-text {
  word-break: break-word;
  line-height: 1.4;
  color: #334155;
  text-align: left;
}
.ipd-desc-clamped {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
}
.ipd-desc-expanded {
  white-space: pre-wrap;
}
.ipd-btn-more {
  padding: 0;
  min-height: auto;
  margin-top: 4px;
  font-size: 12px;
  color: #2563eb;
  text-align: left;
  display: block;
}
.ipd-btn-more:hover {
  text-decoration: underline;
}

/* ─── Banner HĐĐT ─────────────────────────────────── */
.ipd-invoice-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 12px 16px;
  margin: 12px 0 16px 0;
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.05);
  word-break: normal !important;
  word-wrap: break-word !important;
  white-space: normal !important;
}

.ipd-invoice-banner-icon {
  font-size: 20px;
  color: #d97706;
  margin-top: 2px;
}

.ipd-invoice-banner-body {
  flex: 1;
  text-align: left;
}

.ipd-invoice-banner-title {
  font-size: 13px;
  font-weight: 700;
  color: #78350f;
  margin: 0 0 6px 0;
}

.ipd-invoice-banner-text {
  font-size: 12.5px;
  color: #92400e;
  line-height: 1.5;
  margin: 0;
  word-break: normal !important;
  word-wrap: break-word !important;
  white-space: normal !important;

  p {
    margin: 0;
  }
}

/* ─── Profile & Meta (Header) ───────────────────────── */
.ipd-student-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

@media (min-width: 768px) {
  .ipd-student-card {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    gap: 24px;
  }
}

.ipd-student-profile-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

@media (min-width: 768px) {
  .ipd-student-profile-section {
    flex-direction: row;
    align-items: center;
    gap: 32px;
  }
}

.ipd-student-profile {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ipd-student-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #eff6ff;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  border: 1px solid #dbeafe;
}

.ipd-student-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
}

.ipd-student-name {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.ipd-student-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.ipd-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
}

.ipd-badge--code {
  background-color: #f1f5f9;
  color: #475569;
}

.ipd-status-tag {
  border-radius: 6px !important;
  font-weight: 600;
}

.ipd-student-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  border-top: 1px solid #f1f5f9;
  padding-top: 12px;
  width: 100%;
}

@media (min-width: 768px) {
  .ipd-student-info-grid {
    width: auto;
    border-top: none;
    padding-top: 0;
    display: flex;
    gap: 24px;
  }
}

.ipd-info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
}

.ipd-info-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  font-weight: 600;
}

.ipd-info-value {
  font-size: 13.5px;
  font-weight: 700;
  color: #334155;
}

/* ─── Header QR section ─────────────────────────────── */
.ipd-header-qr-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  border-left: 1px dashed #cbd5e1;
  padding-left: 24px;
  flex-shrink: 0;
}

.ipd-header-qr-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-align: center;
  white-space: nowrap;
}

.ipd-header-qr-wrapper {
  width: 80px;
  height: 80px;
  padding: 4px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.ipd-header-qr-wrapper:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.1);
}

.ipd-header-qr-image {
  width: 100%;
  height: 100%;
  border-radius: 3px;

  /deep/ img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

/* ─── Table Grouping & checked rows ──────────────────── */
/deep/ .el-table .ipd-row-checked {
  background-color: #eff6ff !important;
}

/deep/ .el-table .ipd-row-checked:hover > td {
  background-color: #e0f2fe !important;
}

/deep/ .el-table .ipd-row-child {
  background-color: #fafafa !important;
}

/deep/ .el-table .ipd-row-child:hover > td {
  background-color: #f1f5f9 !important;
}

.ipd-parent-name {
  font-weight: 700;
  color: #0f172a;
}

/* ─── Financial Dashboard Cards ─────────────────────── */
.ipd-summary-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ipd-summary-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.ipd-summary-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.ipd-card-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ipd-card-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.ipd-card-label {
  font-size: 12.5px;
  color: #475569;
  font-weight: 600;
}

.ipd-card-value {
  font-size: 13.5px;
  font-weight: 750;
  color: #1e293b;
  text-align: right;
}

/* Card variants */
.ipd-card--neutral {
  border-left: 4px solid #94a3b8;
  .ipd-card-icon { background: #f1f5f9; color: #64748b; }
}

.ipd-card--success {
  border-left: 4px solid #10b981;
  background: linear-gradient(135deg, #ffffff 0%, #ecfdf5 100%);
  .ipd-card-icon { background: #d1fae5; color: #059669; }
  .ipd-card-value { color: #047857; }
}

.ipd-card--danger {
  border-left: 4px solid #ef4444;
  background: linear-gradient(135deg, #ffffff 0%, #fef2f2 100%);
  .ipd-card-icon { background: #fee2e2; color: #dc2626; }
  .ipd-card-value { color: #b91c1c; }
}

.ipd-card--primary {
  border-left: 4px solid #3b82f6;
  .ipd-card-icon { background: #dbeafe; color: #2563eb; }
  .ipd-card-value { color: #1d4ed8; }
}

.ipd-card--success-light {
  border-left: 4px solid #10b981;
  .ipd-card-icon { background: #d1fae5; color: #059669; }
}

.ipd-card--success-dark {
  border-left: 4px solid #059669;
  background: linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%);
  .ipd-card-icon { background: #dcfce7; color: #16a34a; }
  .ipd-card-value { color: #15803d; }
}

.ipd-card--warning-dark {
  border-left: 4px solid #ea580c;
  background: linear-gradient(135deg, #ffffff 0%, #fff7ed 100%);
  .ipd-card-icon { background: #ffedd5; color: #ea580c; }
  .ipd-card-value { color: #c2410c; }
}

.ipd-card--info {
  border-left: 4px solid #06b6d4;
  .ipd-card-icon { background: #ecfeff; color: #0891b2; }
  .ipd-card-value { color: #0e7490; }
}

.ipd-card--highlight {
  border-left: 4px solid #f59e0b;
  background: linear-gradient(135deg, #ffffff 0%, #fffbeb 100%);
  .ipd-card-icon { background: #fef3c7; color: #d97706; }
  .ipd-card-value { color: #b45309; }
}

/* ─── Form Controls & Radios ────────────────────────── */
.ipd-form-item-radio-button /deep/ .el-form-item__content {
  text-align: left;
  margin-top: 4px;
}

.ipd-radio-buttons {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.ipd-radio-buttons /deep/ .el-radio-button__inner {
  font-weight: 600;
  border-color: #cbd5e1;
  color: #475569;
}

.ipd-radio-buttons /deep/ .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background-color: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
  box-shadow: -1px 0 0 0 #2563eb;
}

/* ─── Action Buttons Hierarchy ─────────────────────── */
.ipd-btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
  border: none !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25) !important;
  transition: all 0.2s !important;
}

.ipd-btn-primary:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35) !important;
  transform: translateY(-1px);
}

.ipd-btn-warning-plain {
  font-weight: 600 !important;
  border-color: #f59e0b !important;
  color: #d97706 !important;
  transition: all 0.2s !important;
}

.ipd-btn-warning-plain:hover {
  background-color: #fffbeb !important;
  color: #b45309 !important;
}

.ipd-btn-success-plain {
  font-weight: 600 !important;
  border-color: #10b981 !important;
  color: #059669 !important;
  transition: all 0.2s !important;
}

.ipd-btn-success-plain:hover {
  background-color: #ecfdf5 !important;
  color: #047857 !important;
}

.ipd-btn-close {
  background-color: #f1f5f9 !important;
  border-color: #e2e8f0 !important;
  color: #64748b !important;
  font-weight: 600 !important;
}

.ipd-btn-close:hover {
  background-color: #e2e8f0 !important;
  color: #475569 !important;
}
</style>
